/**
 * The Dashboard's notes peek (v3.97.0) — a 📝 button beside a student's name,
 * shown only when the student has notes. Hovering it with a mouse shows every
 * note about the student, each group under the name of where it was written
 * (studentNotesDigest.js); a tap or click (iPad / iPhone have no hover; the
 * keyboard's Enter / Space) pins it open until Close, Escape or a tap
 * elsewhere. Screen only (`.no-print`). Nothing here writes.
 *
 * Phones and iPads (App.css mobile block, ≤1023px): the panel is a sheet
 * across the bottom of the screen, so it never runs off the side — and hover
 * does not open it there (the pointer could not reach a sheet; WCAG 1.4.13):
 * hover opens it only where HOVER_QUERY matches (a mouse on a wide screen).
 */
import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { noteCountOf } from '../utils/studentNotesDigest';

// A mouse merely crossing the grid opens nothing: the pointer has to rest on
// 📝 for HOVER_OPEN_DELAY_MS (and the panel never covers the next rows'
// buttons by accident — or shows notes on a projected screen in passing).
export const HOVER_OPEN_DELAY_MS = 200;
const HOVER_CLOSE_DELAY_MS = 150;
const PANEL_WIDTH = 320;
export const HOVER_QUERY = '(hover: hover) and (pointer: fine) and (min-width: 1024px)';
const screenCanHover = () => typeof window.matchMedia !== 'function' || window.matchMedia(HOVER_QUERY).matches;
const isHoverPointer = (e) => e.pointerType !== 'touch' && e.pointerType !== 'pen' && screenCanHover();

export default function StudentNotesPeek({ studentName, groups, pendingNote }) {
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [alignRight, setAlignRight] = useState(false);
  const wrapRef = useRef(null);
  const buttonRef = useRef(null);
  const closeTimer = useRef(null);
  const openTimer = useRef(null);
  const panelId = useId();
  const count = noteCountOf(groups);
  const open = hovered || pinned;

  const clearCloseTimer = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const clearOpenTimer = () => {
    if (openTimer.current) {
      clearTimeout(openTimer.current);
      openTimer.current = null;
    }
  };
  useEffect(() => () => { clearCloseTimer(); clearOpenTimer(); }, []);

  const close = useCallback((refocus) => {
    clearCloseTimer();
    clearOpenTimer();
    setHovered(false);
    setPinned(false);
    if (refocus && buttonRef.current) buttonRef.current.focus();
  }, []);
  // Escape returns focus to the button only when the panel was pinned or
  // focus is inside it — a hover-opened panel never pulls focus from elsewhere.
  const focusInside = () => !!wrapRef.current && wrapRef.current.contains(document.activeElement);

  // While pinned: Escape closes (focus back on the button); a press outside closes.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close(pinned || focusInside());
      }
    };
    const onDown = (e) => {
      if (pinned && wrapRef.current && !wrapRef.current.contains(e.target)) close(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [open, pinned, close]);

  // Desktop: open toward the left when the panel would run past the right
  // edge of what scrolls — the app's content area (its clientWidth leaves out
  // its own scrollbar; the document itself never scrolls), else the window.
  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;
    const r = buttonRef.current.getBoundingClientRect();
    const area = buttonRef.current.closest('.content-area');
    const rightEdge = area && area.clientWidth
      ? area.getBoundingClientRect().left + area.clientWidth
      : (document.documentElement.clientWidth || window.innerWidth);
    setAlignRight(r.left + PANEL_WIDTH > rightEdge - 8);
  }, [open]);

  if (count === 0) return null;

  return (
    <span
      ref={wrapRef}
      className="dashboard-notes-peek no-print"
      onPointerEnter={(e) => {
        if (!isHoverPointer(e)) return;
        clearCloseTimer();
        if (hovered) return; // back within the close delay: stays open
        clearOpenTimer();
        openTimer.current = setTimeout(() => { openTimer.current = null; setHovered(true); }, HOVER_OPEN_DELAY_MS);
      }}
      onPointerLeave={(e) => {
        if (!isHoverPointer(e)) return;
        clearOpenTimer();
        clearCloseTimer();
        closeTimer.current = setTimeout(() => setHovered(false), HOVER_CLOSE_DELAY_MS);
      }}
    >
      <button
        ref={buttonRef}
        type="button"
        className="dashboard-notes-btn"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={`Notes for ${studentName} (${count})`}
        onClick={() => { if (pinned) close(false); else setPinned(true); }}
      >
        <span aria-hidden="true">📝</span>
      </button>
      {open && (
        <div
          id={panelId}
          role="region"
          aria-label={`Notes for ${studentName}`}
          className={`dashboard-notes-pop${alignRight ? ' dashboard-notes-pop--right' : ''}`}
        >
          <div className="dashboard-notes-pop-head">
            <strong>{studentName}</strong>
            {pinned && (
              <button type="button" className="dashboard-notes-close" onClick={() => close(true)}>Close</button>
            )}
          </div>
          {groups.map((g) => (
            <section key={g.key} className="dashboard-notes-group">
              <h4>{g.heading}</h4>
              <ul>
                {g.items.map((item) => (
                  <li key={item.id}>
                    {item.meta && <span className="dashboard-notes-meta">{item.meta}</span>}
                    <span className="dashboard-notes-text">{item.text}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
          {pendingNote && <p className="dashboard-notes-pending">{pendingNote}</p>}
        </div>
      )}
    </span>
  );
}
